import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { forkJoin } from 'rxjs';
import { BillInfo, PaymentInfo } from 'src/app/models/models';
import { Bank_Account, Bank_Movement, Store_Bank_Account, User } from 'src/app/models/RestModels';
import { BaseComponent } from 'src/app/pages/base/base.component';
import { Utils } from 'src/app/classes/Utils';

interface PayMovement
{
	bank_account_id: number | null;
	transaction_type: "CASH" | "TRANSFER" | "CREDIT_CARD" | "DEBIT_CARD" | "CHECK";
	amount: number;
	paid_date: string;
	reference: string;
}

@Component({
	selector: 'app-pay-bills',
	templateUrl: './pay-bills.component.html',
	styleUrls: ['./pay-bills.component.css']
})
export class PayBillsComponent extends BaseComponent implements OnInit
{
	@Input() bill_list: BillInfo[] = [];
	@Input() provider: User | null = null;
	@Input() store_id: number | null = null;
	@Output() paymentCreated = new EventEmitter<PaymentInfo>();

	bank_account_list: Bank_Account[] = [];
	store_bank_account_list: Store_Bank_Account[] = [];
	pay_movements: PayMovement[] = [];
	is_loading: boolean = false;

	ngOnInit()
	{
		const storeId = this.store_id || this.rest.current_user.store_id;

		this.subs.sink = forkJoin({
			bank_accounts: this.rest.bank_account.search({limit: 9999}),
			store_bank_accounts: this.rest.store_bank_account.search({limit: 9999}),
		}).subscribe({
			next: (response: any) =>
			{
				this.bank_account_list = response.bank_accounts.data;
				this.store_bank_account_list = response.store_bank_accounts.data;
				this.initMovements();
			},
			error: (error: any) => this.showError(error),
		});
	}

	initMovements()
	{
		if (this.pay_movements.length) return;

		const total = this.bill_list.reduce((s, bi) => s + (bi.bill.total - bi.bill.amount_paid), 0);
		this.pay_movements.push({
			bank_account_id: null,
			transaction_type: null,
			amount: Math.round(total * 100) / 100,
			paid_date: Utils.getUTCMysqlStringFromDate(new Date()).substring(0, 10),
			reference: '',
		});
	}

	getFilteredAccountsForMovement(txType: string): Bank_Account[]
	{
		const storeId = this.store_id || this.rest.current_user.store_id;
		if (!storeId || !txType || !this.store_bank_account_list.length)
			return this.bank_account_list;

		const matchingIds = this.store_bank_account_list
			.filter(sba => sba.store_id === storeId && sba.default_transaction_type === txType)
			.map(sba => sba.bank_account_id);

		if (matchingIds.length)
			return this.bank_account_list.filter(ba => matchingIds.includes(ba.id));

		return this.bank_account_list;
	}

	addMovement()
	{
		this.pay_movements.push({
			bank_account_id: null,
			transaction_type: null,
			amount: 0,
			paid_date: Utils.getUTCMysqlStringFromDate(new Date()).substring(0, 10),
			reference: '',
		});
	}

	removeMovement(idx: number)
	{
		if (this.pay_movements.length > 1)
			this.pay_movements.splice(idx, 1);
	}

	getPaymentTotal(): number
	{
		return this.pay_movements.reduce((s, pm) => s + (pm.amount || 0), 0);
	}

	getTotalPending(): number
	{
		return this.bill_list.reduce((s, bi) => s + (bi.bill.total - bi.bill.amount_paid), 0);
	}

	submitPayment()
	{
		if (this.is_loading) return;

		const total = this.getPaymentTotal();
		if (total <= 0) { this.showError('El monto debe ser mayor a 0'); return; }

		for (let pm of this.pay_movements)
		{
			if (!pm.transaction_type) { this.showError('Selecciona método de pago en todos los movimientos'); return; }
			if (!pm.amount || pm.amount <= 0) { this.showError('Todos los movimientos necesitan un monto mayor a 0'); return; }
		}

		const firstBill = this.bill_list[0]?.bill;
		const currencyId = firstBill?.currency_id || 'MXN';

		this.is_loading = true;

		const concept = this.bill_list.map(bi => 'Recibo #' + bi.bill.id).join(', ');

		const paymentInfo: Partial<PaymentInfo> = {
			payment: {
				type: 'expense',
				tag: 'Compra',
				payment_amount: total,
				concept: 'Pago a: ' + concept,
				received_amount: total,
				facturado: 'NO',
				change_amount: 0,
				exchange_rate: 1,
				currency_id: currencyId,
				paid_by_user_id: this.rest.current_user.id,
				sat_pdf_attachment_id: null,
				sat_factura_id: null,
				sat_xml_attachment_id: null,
				sync_id: this.rest.getSyncId()
			},
			movements: this.pay_movements.map(pm =>
			{
				const movementsForThisType = this.distributeAmountAcrossBills(pm.amount, pm.transaction_type, pm.bank_account_id, pm.paid_date, pm.reference, currencyId);
				return movementsForThisType;
			}).flat()
		};

		this.subs.sink = this.rest.makePayment(paymentInfo, null)
			.subscribe({
				next: (result: PaymentInfo) =>
				{
					this.is_loading = false;
					this.showSuccess('Pago registrado');
					this.paymentCreated.emit(result);
				},
				error: (error: any) =>
				{
					this.is_loading = false;
					this.showError(error);
				}
			});
	}

	private distributeAmountAcrossBills(totalAmount: number, txType: string, bankAccountId: number | null, paidDate: string, reference: string, currencyId: string): any[]
	{
		const bills = this.bill_list.filter(bi => (bi.bill.total - bi.bill.amount_paid) > 0);
		const movements: any[] = [];

		let remaining = totalAmount;

		for (let i = 0; i < bills.length && remaining > 0.001; i++)
		{
			const bi = bills[i];
			const pending = Math.round((bi.bill.total - bi.bill.amount_paid) * 100) / 100;
			const amount = remaining <= pending ? remaining : pending;
			remaining = Math.round((remaining - amount) * 100) / 100;

			movements.push({
				bank_movement: {
					id: null,
					amount_received: amount,
					bank_account_id: bankAccountId,
					card_ending: null,
					client_user_id: null,
					created: new Date(),
					currency_id: currencyId,
					invoice_attachment_id: null,
					note: null,
					origin_bank_name: null,
					paid_date: paidDate,
					payment_id: null,
					provider_user_id: bi.bill.provider_user_id || null,
					receipt_attachment_id: null,
					received_by_user_id: null,
					reference: reference,
					status: 'ACTIVE',
					exchange_rate: 1,
					total: amount,
					transaction_type: txType,
					type: 'expense',
					updated: new Date(),
				} as Bank_Movement,
				bank_movement_bills: [{
					bank_movement_bill: {
						amount: amount,
						bill_id: bi.bill.id,
						currency_amount: amount,
						currency_id: currencyId,
						exchange_rate: 1,
						status: 'ACTIVE',
					}
				}]
			});
		}

		return movements;
	}

	getTransactionTypeLabel(type: string): string
	{
		const labels: Record<string, string> = {
			'CASH': 'Efectivo', 'TRANSFER': 'Transferencia', 'CHECK': 'Cheque',
			'CREDIT_CARD': 'T. Crédito', 'DEBIT_CARD': 'T. Débito',
		};
		return labels[type] || type;
	}
}
